/**
 * Solana Native Staking Protocol
 *
 * Provides native staking through validator delegation using Blockdaemon Staking API v2.
 * Supports mainnet and testnet networks.
 */
import { SolanaStakingSdk } from "@blockdaemon/solana-staking-sdk";
import { Api as EStakeIntegrationApi } from "@blockdaemon/stake-integration-api";
import { normalizeChainIdString } from "../../../../shared/caip2";
import { DEFAULT_BASE_PATH } from "../../../../shared/constants";
import { ValidatorNotWhitelistedError } from "../../../../shared/error";
import { executeGenericOperation } from "../../../../shared/executor";
import { VAULT_ADDRESSES_MAP } from "../../../../shared/vault-addresses";
import { StakingProtocol } from "../../staking-protocol";
import { _getStakeAccount, _getStakeBalance } from "./internal/balance";
import { _buildDeactivateTx } from "./internal/deactivate";
import { _buildStakeTx, _estimateStakeFee } from "./internal/deposit";
import { GetSolanaAPY } from "./internal/rewards";
import { _buildWithdrawTx } from "./internal/unstake";
import { APY_DEFAULT_LOOKBACK_SECONDS, _isValidatorVoteKeyAllowed, createSolanaStakingReportingApi, } from "./internal/util";
import { getSolanaNetworkName } from "./solana-types";
export class SolanaStakingProtocol extends StakingProtocol {
    // we lazy initialize the clients on the first API use, to accommodate possibility of empty API keys
    _stakeApiClientMainnet;
    _stakeApiClientTestnet;
    _stakeApiSdkMainnet;
    _stakeApiSdkTestnet;
    _stakingReportingApi;
    _effectiveBasePath;
    _bdApiKey;
    constructor(options) {
        super(options);
        if (!options.bdApiKey) {
            this.logger.warn("Blockdaemon API key is not set. Some Solana staking operations may fail.");
        }
        this._bdApiKey = options.bdApiKey ?? "";
        this._effectiveBasePath = options.basePath || DEFAULT_BASE_PATH;
    }
    createApiClientConfig(network) {
        return {
            baseURL: this._effectiveBasePath,
            headers: {
                "X-API-Key": this._bdApiKey,
                "Content-Type": "application/json",
                "X-Network": network,
            },
        };
    }
    get stakeApiClientMainnet() {
        if (!this._stakeApiClientMainnet) {
            this._stakeApiClientMainnet = new EStakeIntegrationApi(this.createApiClientConfig("mainnet"));
        }
        return this._stakeApiClientMainnet;
    }
    get stakeApiClientTestnet() {
        if (!this._stakeApiClientTestnet) {
            this._stakeApiClientTestnet = new EStakeIntegrationApi(this.createApiClientConfig("testnet"));
        }
        return this._stakeApiClientTestnet;
    }
    get stakeApiSdkMainnet() {
        if (!this._stakeApiSdkMainnet) {
            this._stakeApiSdkMainnet = new SolanaStakingSdk({
                rpcHttpEndpoint: `${this._effectiveBasePath}/solana/mainnet/native`,
                rpcHttpAuth: this._bdApiKey,
                rpcWsAuth: this._bdApiKey,
                external: true,
            });
        }
        return this._stakeApiSdkMainnet;
    }
    get stakeApiSdkTestnet() {
        if (!this._stakeApiSdkTestnet) {
            this._stakeApiSdkTestnet = new SolanaStakingSdk({
                rpcHttpEndpoint: `${this._effectiveBasePath}/solana/testnet/native`,
                rpcHttpAuth: this._bdApiKey,
                rpcWsAuth: this._bdApiKey,
                external: true,
            });
        }
        return this._stakeApiSdkTestnet;
    }
    /**
     * Blockdaemon Staking Reporting API (internal). Shared by:
     * - {@link getStakingMetadata} — pre-aggregated yield stats (`GetSolanaStats`) for the get-validator-info flow
     * - {@link getStakeBalance} — delegator lifetime rewards for `totalEarnedAssets` (`GetSolanaDelegatorRewards` in balance)
     */
    get stakingReportingApi() {
        if (!this._stakingReportingApi) {
            this._stakingReportingApi = createSolanaStakingReportingApi(this._bdApiKey, this._effectiveBasePath, this.logger);
        }
        return this._stakingReportingApi;
    }
    /** Returns the API client instance that matches the given chain ID. */
    getClientForChain(chainId) {
        const network = normalizeChainIdString(chainId);
        return network === normalizeChainIdString("solana:mainnet")
            ? this.stakeApiClientMainnet
            : this.stakeApiClientTestnet;
    }
    /** Returns the SDK instance that matches the given chain ID. */
    getSdkForChain(chainId) {
        const network = normalizeChainIdString(chainId);
        return network === normalizeChainIdString("solana:mainnet")
            ? this.stakeApiSdkMainnet
            : this.stakeApiSdkTestnet;
    }
    /**
     * Validates that a chain ID is supported by this protocol.
     * Normalizes the chain ID to canonical CAIP-2 format before checking.
     * @throws Error if chain is not supported
     */
    validateChainSupported(chainId) {
        const normalizedChainId = normalizeChainIdString(chainId);
        if (!this.availableChains.includes(normalizedChainId)) {
            throw new Error(`Chain ${chainId} is not supported by ${this.name} protocol`);
        }
    }
    /** Builds a stake transaction for delegating SOL to a validator. */
    async buildStakeTx(request) {
        const chainId = request.chainId;
        const normalizedChainId = normalizeChainIdString(chainId);
        this.validateChainSupported(chainId);
        const validatorAddress = request.vaultAddress || VAULT_ADDRESSES_MAP[normalizedChainId];
        if (!validatorAddress) {
            throw new Error(`Validator address (vaultAddress) is required for Solana staking on chain ${chainId}`);
        }
        const network = this.chainService.getSolanaNetwork(chainId);
        if (!_isValidatorVoteKeyAllowed(network, validatorAddress, this.protocolConfig?.solana)) {
            throw new ValidatorNotWhitelistedError(validatorAddress, network);
        }
        const requestWithParsedValidatorAddress = {
            ...request,
            vaultAddress: validatorAddress,
        };
        return executeGenericOperation("buildStakeTx", this.logger, _buildStakeTx(this.logger, this.chainService, this.getClientForChain(chainId), requestWithParsedValidatorAddress, this.protocolConfig?.solana));
    }
    /**
     * Estimates stake transaction fee. Returns 0 as fees are in API response.
     * @throws Error always - fee estimation not implemented for Solana
     */
    async estimateDepositGas(request) {
        return _estimateStakeFee(this.logger, request.chainId);
    }
    /** Gets Solana network from chain ID. Exposed for testing. */
    getSolanaNetworkFromChainId(chainId) {
        return this.chainService.getSolanaNetwork(chainId);
    }
    /**
     * Builds Solana withdrawal transaction of staked assets.
     * Returns an unsigned deactivation transaction as base64 in the `data` field.
     */
    async buildWithdrawTx(request) {
        this.logger.debug("Withdrawal on Solana", {
            chainId: request.chainId,
            userAddress: request.userAddress,
            amount: request.amount.toString(),
        });
        const chainId = request.chainId;
        try {
            this.validateChainSupported(chainId);
            const stakeAccountAddress = request.vaultAddress;
            if (!stakeAccountAddress) {
                throw new Error(`Stake account address (vaultAddress) is required for Solana withdrawal on chain ${chainId}`);
            }
            this.logger.debug("Building Solana withdraw transaction", {
                chainId,
                userAddress: request.userAddress,
                stakeAccountAddress,
                amount: request.amount.toString(),
            });
        }
        catch (error) {
            this.logger.error("Failed to build Solana withdraw transaction", {
                error: error instanceof Error ? error.message : "Unknown error",
                request: {
                    amount: request.amount,
                    vaultAddress: request.vaultAddress,
                    userAddress: request.userAddress,
                    chainId: request.chainId,
                },
            });
            throw new Error(`Failed to build Solana withdraw transaction: ${error instanceof Error ? error.message : "Unknown error"}`);
        }
        return executeGenericOperation("buildWithdrawTx", this.logger, _buildWithdrawTx(this.logger, this.chainService, this.getClientForChain(chainId), request));
    }
    /** @throws {Error} Not yet implemented for Solana native staking. */
    async getMaxWithdraw(_request) {
        throw new Error("Solana getMaxWithdraw not yet implemented");
    }
    /** @throws {Error} Not applicable for Solana native staking. */
    async convertVaultAssetsToShares(_request) {
        throw new Error("convertVaultAssetsToShares is not supported for Solana native staking");
    }
    /** @throws {Error} Not yet implemented for Solana native staking. */
    async getExitQueuePositions(_request) {
        throw new Error("Solana getExitQueuePositions not yet implemented");
    }
    /** @throws {Error} Not yet implemented for Solana native staking. */
    async claimExitQueueTx(_request) {
        throw new Error("Solana claimExitQueueTx not yet implemented");
    }
    /**
     * Gets stake balance and all stake accounts for a Solana address.
     *
     * @param request - The request containing userAddress and chainId
     * @returns Promise resolving to stake balance with Solana-specific metadata
     *
     * @remarks
     * Queries the Blockdaemon Stake Integration API to retrieve all stake accounts
     * delegated by the specified address. Returns total staked amount in lamports
     * along with detailed metadata for each stake account including validator,
     * activation state, and withdrawal eligibility.
     *
     * The metadata ({@link ISolanaBalanceMetadata}) includes:
     * - `stakeAccounts`: Array of {@link ISolanaStakeAccount} with per-account details
     * - `withdrawableCount`: Number of accounts ready for withdrawal
     * - `deactivatableCount`: Number of active accounts that can be deactivated
     *
     * `totalEarnedAssets` is populated from the same internal Blockdaemon Staking Reporting API used for
     * validator APY in {@link getStakingMetadata} (delegator rewards; defaults to 0 if the call fails).
     *
     * @example
     * ```typescript
     * const balance = await sdk.staking.getStakeBalance({
     *   userAddress: "5d4VNtgPbYJMttEGsfGnCPruGFHYymTQnkiyBmKMcJY1",
     *   chainId: "solana:mainnet"
     * });
     *
     * console.log(`Total staked: ${balance.assets} lamports`);
     * console.log(`Asset: ${balance.asset.symbol}`); // "SOL"
     *
     * const metadata = balance.metadata as ISolanaBalanceMetadata;
     * console.log(`Stake accounts: ${metadata.stakeAccounts.length}`);
     * console.log(`Withdrawable: ${metadata.withdrawableCount}`);
     * console.log(`Deactivatable: ${metadata.deactivatableCount}`);
     *
     * for (const account of metadata.stakeAccounts) {
     *   console.log(`  ${account.stakeAccountAddress}: ${account.stakedAmount} lamports`);
     *   console.log(`    Validator: ${account.validatorAddress}`);
     *   console.log(`    Withdrawable: ${account.isWithdrawable}`);
     * }
     * ```
     *
     * @throws {Error} If the chainId is not supported by this protocol
     */
    async getStakeBalance(request) {
        const chainId = request.chainId;
        this.validateChainSupported(chainId);
        return executeGenericOperation("getStakeBalance", this.logger, _getStakeBalance(this.logger, this.chainService, this.getClientForChain(chainId), request, this.stakingReportingApi));
    }
    /**
     * Gets a single Solana stake account by its public key address.
     *
     * @remarks
     * Queries the Blockdaemon Stake Integration API for the status of a specific
     * stake account pubkey. Useful when a widget already holds the account address
     * from a prior {@link getStakeBalance} call and needs to refresh a single account
     * rather than refetching all accounts for the delegator.
     *
     * @param request - The request containing stakeAccountAddress and chainId
     * @returns Promise resolving to the stake account details
     *
     * @example
     * ```typescript
     * const result = await solanaProtocol.getStakeAccount({
     *   stakeAccountAddress: "6rHbL9Q4...",
     *   chainId: "solana:mainnet",
     * });
     *
     * console.log(`Staked: ${result.account.stakedAmount} lamports`);
     * console.log(`Withdrawable: ${result.account.isWithdrawable}`);
     * ```
     *
     * @throws {Error} If the chainId is not supported by this protocol
     * @throws {Error} If the stakeAccountAddress is not a valid Solana address
     * @throws {Error} If no stake account is found for the given address
     */
    async getStakeAccount(request) {
        const chainId = request.chainId;
        this.validateChainSupported(chainId);
        return executeGenericOperation("getStakeAccount", this.logger, _getStakeAccount(this.logger, this.chainService, this.getClientForChain(chainId), request));
    }
    /** @throws {Error} Not yet implemented for Solana native staking. */
    async getStakerActions(_request) {
        throw new Error("Solana getStakerActions not yet implemented");
    }
    /**
     * Prepares a stake position for withdrawal by deactivating the stake.
     *
     * @remarks
     * For Solana native staking, this corresponds to the `deactivateStake` operation
     * which deactivates the stake delegation. After deactivation and a cooldown period
     * (approximately 2-3 days / ~2 epochs), the stake can be withdrawn.
     *
     * @param request - The request containing stakeAccountAddress, userAddress, and chainId
     * @returns Promise resolving to deactivate transaction details
     *
     * @example
     * ```typescript
     * const result = await sdk.staking.prepareToWithdraw({
     *   userAddress: "DelegatorPubkey...",
     *   stakeAccountAddress: "StakeAccountPubkey...",
     *   chainId: "solana:mainnet"
     * });
     *
     * console.log(`Transaction data: ${result.data}`);
     * console.log(`Deactivation epoch: ${result.metadata?.deactivationEpoch}`);
     * ```
     *
     * @throws {Error} If the chainId is not supported by this protocol
     * @throws {Error} If stakeAccountAddress is not provided
     */
    async prepareToWithdraw(request) {
        const chainId = request.chainId;
        this.validateChainSupported(chainId);
        if (!request.stakeAccountAddress) {
            throw new Error("stakeAccountAddress is required for Solana prepareToWithdraw");
        }
        return executeGenericOperation("prepareToWithdraw", this.logger, _buildDeactivateTx(this.logger, this.chainService, this.getSdkForChain(chainId), request));
    }
    /**
     * @throws {Error} Not yet implemented for Solana native staking.
     */
    async getUserRewards(_request) {
        throw new Error("Solana getUserRewards not yet implemented");
    }
    /** @throws {Error} Not yet implemented for Solana native staking. */
    async claimRewards(_request) {
        throw new Error("Solana claimRewards not yet implemented");
    }
    /** @throws {Error} Not yet implemented for Solana native staking. */
    async estimateClaimRewardsGas(_request) {
        throw new Error("Solana estimateClaimRewardsGas not yet implemented");
    }
    /**
     * Initiates unstaking of staked assets.
     * @throws {Error} Not yet implemented for Solana native staking.
     */
    async unstake(_request) {
        throw new Error("Solana unstake not yet implemented");
    }
    /**
     * Retrieves Solana validator information as a unified staking metadata response.
     *
     * @remarks
     * Calls `getVoteAccounts` via ChainService and fetches pre-aggregated 30-day and 90-day
     * yield stats from the Blockdaemon Staking Reporting API.
     * Results are mapped into the common {@link IGetStakingMetadataResponse} with
     * normalised `apyPercentage` (from the 30-day yield window) and `feePercentage`
     * (from the validator's commission).
     *
     * @param request - Operator request; `operatorAddress` is the validator vote pubkey
     * @returns Promise resolving to operator information with Solana-specific detail
     *
     * @example
     * ```typescript
     * const info = await solanaProtocol.getStakingMetadata({
     *   chainId: "solana:mainnet",
     *   operatorAddress: "FQwewNXahV7MiZcLpY6p1xhUs2acVGQ3U5Xxc7FzV571",
     *   options: { commitment: "finalized" },
     * });
     *
     * console.log(`APY: ${info.apyPercentage}%`);
     * if (info.detail.type === "solana") {
     *   console.log(info.detail.rpcData.current[0].commission);
     * }
     * ```
     */
    async getStakingMetadata(request) {
        const chainId = request.chainId;
        this.validateChainSupported(chainId);
        const validatorAddress = request.operatorAddress?.trim() || VAULT_ADDRESSES_MAP[normalizeChainIdString(chainId)];
        if (!validatorAddress) {
            throw new Error(`Validator address (operatorAddress) is required for Solana staking on chain ${chainId}`);
        }
        this.logger.debug("Fetching Solana staking metadata", {
            validatorAddress,
            chainId,
        });
        // Fetch vote account data via RPC
        const rpcOptions = request.options;
        const rpcResponse = (await this.chainService.getSolanaVoteAccounts(chainId, validatorAddress, rpcOptions));
        // Fetch APY from Blockdaemon Staking Reporting API (hardcoded 30-day look-back window)
        let apy;
        try {
            const network = this.chainService.getSolanaNetwork(chainId);
            const networkName = getSolanaNetworkName(network);
            const now = Math.floor(Date.now() / 1000);
            const targetTimestamp = now - APY_DEFAULT_LOOKBACK_SECONDS;
            apy = await GetSolanaAPY(this.stakingReportingApi, this.logger, {
                address: validatorAddress,
                network: networkName,
                type: "delegator",
                startTime: targetTimestamp,
                endTime: now,
            });
        }
        catch (error) {
            this.logger.warn("Failed to fetch APY data, continuing without it", {
                error: error instanceof Error ? error.message : String(error),
                validatorAddress,
            });
        }
        // Extract normalised metrics
        const apyPercentage = apy?.apyPercentage ? Number.parseFloat(apy.apyPercentage) : undefined;
        const bdValidator = rpcResponse.current.find((v) => v.votePubkey === validatorAddress);
        const feePercentage = bdValidator?.commission;
        return {
            operatorAddress: validatorAddress,
            protocol: this.name,
            apyPercentage,
            feePercentage,
            detail: {
                type: "solana",
                rpcData: rpcResponse,
                apy,
            },
        };
    }
}
//# sourceMappingURL=data:application/json;base64,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